import { useState } from 'react';
import { AdminLayout } from './AdminLayout';
import {
  Rocket, Users, BookOpen, Shield, Mail, Video, MonitorPlay,
  UserCheck, BarChart3, Settings, CreditCard, Brain, Globe,
  FileText, Lock, Zap, ChevronDown, ChevronRight, ExternalLink,
  Target, Briefcase, Star, Flame, Trophy, Crown, Award,
  MessageSquare, Inbox, Calendar
} from 'lucide-react';

const Section = ({ icon: Icon, title, children, defaultOpen = false }) => {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div className="border border-white/[0.06] rounded-xl overflow-hidden mb-3">
      <button onClick={() => setOpen(!open)}
        className="w-full flex items-center gap-3 p-4 hover:bg-white/[0.02] transition-colors text-left">
        <div className="w-9 h-9 bg-rtb-purple/10 rounded-lg flex items-center justify-center flex-shrink-0">
          <Icon className="w-5 h-5 text-rtb-purple" />
        </div>
        <h3 className="font-oswald font-bold text-white flex-1">{title}</h3>
        {open ? <ChevronDown className="w-4 h-4 text-white/30" /> : <ChevronRight className="w-4 h-4 text-white/30" />}
      </button>
      {open && <div className="px-5 pb-5 pt-1 border-t border-white/[0.04]">{children}</div>}
    </div>
  );
};

const Feature = ({ title, desc }) => (
  <div className="flex items-start gap-2.5 py-2">
    <div className="w-1.5 h-1.5 bg-rtb-purple rounded-full mt-2 flex-shrink-0" />
    <div>
      <span className="text-white/80 text-sm font-medium">{title}</span>
      {desc && <span className="text-white/40 text-sm"> — {desc}</span>}
    </div>
  </div>
);

const TableRow = ({ cells, header = false }) => (
  <tr className={header ? 'border-b border-white/[0.08]' : 'border-b border-white/[0.04] last:border-0'}>
    {cells.map((c, i) => header
      ? <th key={i} className="text-left text-white/50 text-xs font-bold uppercase tracking-wide py-2 px-3">{c}</th>
      : <td key={i} className="text-white/60 text-sm py-2.5 px-3">{c}</td>
    )}
  </tr>
);

export default function AdminFeatures() {
  return (
    <AdminLayout title="Platform Features">
      <p className="text-white/40 text-sm mb-6">Complete feature reference for the ReadyToBuildIt.life ecosystem. Share this with stakeholders or use it as a training resource.</p>

      {/* ===== PARTICIPANT JOURNEY ===== */}
      <div className="mb-6">
        <h2 className="font-oswald text-white/20 text-xs uppercase tracking-widest mb-3 pl-1">Participant Experience</h2>

        <Section icon={Globe} title="Landing Page" defaultOpen={true}>
          <Feature title="AI Marketing Video" desc="Sora 2 AI-generated 12-second promo video that auto-plays" />
          <Feature title="4-Phase Program Overview" desc="Plan (1-5), Prepare (6-10), Prosper (11-15), Scale (16-20)" />
          <Feature title="What's Included" desc="10 program benefits: LLC formation, AI fee waivers, credit building, $250K funding access, mastery sessions, and more" />
          <Feature title="Social Proof" desc="Stat counters — 984+ families, 50 states, AI Assistant, $0 Filing Target" />
          <Feature title="Social Media Links" desc="Facebook and Instagram (configured in Admin Settings)" />
          <Feature title="Legal Footer" desc="Terms of Service, Privacy Policy, About Us" />
        </Section>

        <Section icon={Users} title="Registration & Onboarding (4 Steps)">
          <div className="space-y-4">
            <div className="p-3 bg-white/[0.03] rounded-lg">
              <p className="text-rtb-purple text-xs font-bold uppercase tracking-wide mb-1">Step 1 — Questionnaire</p>
              <p className="text-white/50 text-sm">Multi-question business assessment. AI scores answers and generates a personalized Recommended Business Path (Real Estate, Online, Service, Consulting).</p>
            </div>
            <div className="p-3 bg-white/[0.03] rounded-lg">
              <p className="text-rtb-purple text-xs font-bold uppercase tracking-wide mb-1">Step 2 — Payment ($75 via Whop)</p>
              <p className="text-white/50 text-sm">Participant pays on Whop, returns with their license key. System performs <strong className="text-white/70">server-side Whop API validation</strong> — strict license key matching. Fake codes rejected.</p>
            </div>
            <div className="p-3 bg-white/[0.03] rounded-lg">
              <p className="text-rtb-purple text-xs font-bold uppercase tracking-wide mb-1">Step 3 — Contract Signing</p>
              <p className="text-white/50 text-sm">Full digital mentorship agreement. Optional government ID upload. Electronic signature. Auto-sends Welcome Letter via Brevo email on completion.</p>
            </div>
            <div className="p-3 bg-white/[0.03] rounded-lg">
              <p className="text-rtb-purple text-xs font-bold uppercase tracking-wide mb-1">Step 4 — Welcome & Launch</p>
              <p className="text-white/50 text-sm">Personalized welcome, access to materials (Financial Literacy video, Ebook, Mastery Session booking). "Start My 20-Day Journey" button.</p>
            </div>
          </div>
        </Section>

        <Section icon={BookOpen} title="20-Day Program & Gamification">
          <p className="text-white/50 text-sm mb-3">Each of the 20 days has a title, description, phase label, and a "Complete" button that unlocks the next day.</p>
          <div className="overflow-x-auto mb-4">
            <table className="w-full text-sm">
              <thead><TableRow header cells={['Phase', 'Days', 'Focus']} /></thead>
              <tbody>
                <TableRow cells={['Foundation', '1-5', 'Mindset, business path, LLC formation, branding, EIN & bank account']} />
                <TableRow cells={['Structure', '6-10', 'Financial literacy, credit building, business plan, funding ($250K), legal']} />
                <TableRow cells={['Activation', '11-15', 'Marketing, client acquisition, first revenue, networking, automation']} />
                <TableRow cells={['Expansion', '16-20', 'Growth strategy, team building, multiple revenue streams, legacy, graduation']} />
              </tbody>
            </table>
          </div>
          <p className="text-white/50 text-xs font-bold uppercase tracking-wide mb-2">Gamification</p>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-2 mb-3">
            {[
              { icon: Rocket, label: 'First Step', day: 1, color: 'text-blue-400' },
              { icon: Shield, label: 'LLC Initiated', day: 3, color: 'text-rtb-purple' },
              { icon: Flame, label: 'Week 1 Complete', day: 5, color: 'text-orange-400' },
              { icon: Trophy, label: 'Halfway Hero', day: 10, color: 'text-amber-400' },
              { icon: Zap, label: 'Activator', day: 15, color: 'text-rtb-green' },
              { icon: Crown, label: 'Graduate', day: 20, color: 'text-yellow-400' },
            ].map(m => (
              <div key={m.day} className="flex items-center gap-2 p-2 bg-white/[0.03] rounded-lg">
                <m.icon className={`w-4 h-4 ${m.color}`} />
                <div>
                  <p className="text-white/70 text-xs font-medium">{m.label}</p>
                  <p className="text-white/30 text-[10px]">Day {m.day}</p>
                </div>
              </div>
            ))}
          </div>
          <Feature title="XP Counter" desc="50 XP per completed day (max 1,000 XP)" />
          <Feature title="Streak Tracker" desc="Counts consecutive completed days" />
          <Feature title="Progress Bar" desc="Visual gradient bar showing completion %" />
        </Section>

        <Section icon={Shield} title="LLC Formation Workflow">
          <Feature title="All 50 States + DC" desc="State-specific filing fees, agency info, fee waiver availability" />
          <Feature title="AI-Powered LLC Assistant" desc="GPT-4o generates step-by-step filing guides, fee waiver/hardship letters, operating agreements, and EIN guidance" />
          <Feature title="Status Tracking" desc="not started → in progress → documents filed → approved" />
          <Feature title="7-Step Process" desc="Choose name, registered agent, articles of organization, EIN application, operating agreement, bank account, compliance" />
        </Section>

        <Section icon={MonitorPlay} title="Meetings (Participant View)">
          <Feature title="Upcoming Sessions" desc="See scheduled video meetings with title, description, date/time" />
          <Feature title="Breakout Rooms" desc="Click individual room links to join a specific breakout" />
          <Feature title="Join Meeting" desc="One-click Jitsi Meet link — no account needed" />
          <Feature title="Mastery Session Booking" desc="Calendly link for 1-on-1 sessions (if configured)" />
        </Section>
      </div>

      {/* ===== COMMUNICATIONS ===== */}
      <div className="mb-6">
        <h2 className="font-oswald text-white/20 text-xs uppercase tracking-widest mb-3 pl-1">Automated Communications</h2>

        <Section icon={Mail} title="Email & SMS System">
          <Feature title="20-Day Drip Sequence" desc="Pre-written daily HTML emails sent via Brevo — each day's lesson, task, and motivation with portal CTA" />
          <Feature title="Welcome Letter" desc="Auto-sent on contract signing" />
          <Feature title="Background Check Emails" desc="Invitation + status updates (cleared/review needed)" />
          <Feature title="Password Reset" desc="Token-based secure reset flow" />
          <Feature title="Lead Notifications" desc="Admin alerted on new qualified leads" />
          <Feature title="SMS" desc="Brevo API messaging for time-sensitive communications" />
        </Section>

        <Section icon={Brain} title="AI Email Auto-Responder">
          <Feature title="Inbound Parsing" desc="Brevo captures replies to reply.readytobuildit.life" />
          <Feature title="AI Classification" desc="GPT-4o reads each email and classifies as 'auto' (can auto-answer) or 'review' (needs admin)" />
          <Feature title="Draft Generation" desc="AI writes a professional, context-aware response" />
          <Feature title="Admin Approval" desc="Admin sees all in the AI Inbox — approve, edit, or dismiss drafts" />
        </Section>
      </div>

      {/* ===== ADMIN TOOLS ===== */}
      <div className="mb-6">
        <h2 className="font-oswald text-white/20 text-xs uppercase tracking-widest mb-3 pl-1">Admin Tools</h2>

        <Section icon={BarChart3} title="Dashboard & Analytics">
          <Feature title="Real-Time Stats" desc="Total participants, leads, paid, active, onboarding, LLC progress" />
          <Feature title="Email Analytics" desc="Delivery tracking — sent, delivered, opened, clicked, bounced, spam" />
          <Feature title="Activity Feed" desc="Recent system events and user actions" />
        </Section>

        <Section icon={Users} title="Participant Management">
          <Feature title="Full Participant Table" desc="Name, contact, status badge, progress bar (0/20), phase, join date" />
          <Feature title="Search & Filter" desc="By name/email/phone. Filters: All, Onboarding, Paid, Contract Signed, Active" />
          <Feature title="CSV Export" desc="One-click download of all participant data" />
          <Feature title="Individual Profiles" desc="Click into any participant for full details" />
        </Section>

        <Section icon={MonitorPlay} title="Video Meetings (Jitsi Meet)">
          <Feature title="Create Meetings" desc="Title, description, scheduled date/time — auto-generates Jitsi link" />
          <Feature title="Breakout Rooms" desc="Add named rooms (e.g., 'Group A - LLC Help'). Admin moves participants between rooms during session" />
          <Feature title="Copy Link" desc="Share join URL with participants instantly" />
          <Feature title="No API Key Needed" desc="Free, end-to-end encrypted, no participant accounts required" />
        </Section>

        <Section icon={UserCheck} title="Background Checks (Checkr)">
          <Feature title="Admin-Initiated" desc="Not mandatory for enrollment — offered as an optional service to specific participants" />
          <Feature title="3 Screening Packages" desc="Basic Criminal, Standard (+ County), Professional (+ MVR + Education + Employment)" />
          <Feature title="Hosted Apply Flow" desc="Checkr emails the participant directly. They enter SSN/DOB on Checkr's secure FCRA-compliant platform. RTBI never touches sensitive PII" />
          <Feature title="Webhook Updates" desc="Real-time status: Invited → Processing → Cleared / Needs Review" />
          <Feature title="Automated Notifications" desc="Email to participant on completion. Admin notified on review-needed" />
        </Section>

        <Section icon={Video} title="AI Video Testimonials (HeyGen)">
          <Feature title="AI Avatar Generation" desc="Create realistic talking-head testimonial videos from custom scripts" />
          <Feature title="Pipeline Tracking" desc="Submitted → Processing → Completed with download URLs" />
          <Feature title="Admin Configuration" desc="HeyGen API key managed in Settings" />
        </Section>

        <Section icon={Inbox} title="AI Email Inbox">
          <Feature title="Inbound Email View" desc="Gmail-like interface showing sender, subject, date, classification" />
          <Feature title="AI Draft Responses" desc="Auto-generated professional replies ready for admin approval" />
          <Feature title="Classification Filters" desc="Auto-respondable vs. needs-review" />
        </Section>

        <Section icon={Settings} title="Settings Panel">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead><TableRow header cells={['Section', 'Configuration']} /></thead>
              <tbody>
                <TableRow cells={['Notifications', 'Admin email, Brevo API key, Brevo Webhook Secret']} />
                <TableRow cells={['Calendar', 'Calendly intro link, Calendly mastery session link']} />
                <TableRow cells={['Payment', 'Whop link, Whop API key, Whop Webhook Secret, Stripe keys']} />
                <TableRow cells={['Social', 'Facebook page, Instagram handle']} />
                <TableRow cells={['AI Video', 'HeyGen API key']} />
                <TableRow cells={['Background Checks', 'Checkr API key, Screening package, Webhook URL']} />
                <TableRow cells={['Admin Security', 'Password change']} />
              </tbody>
            </table>
          </div>
        </Section>
      </div>

      {/* ===== SECURITY ===== */}
      <div className="mb-6">
        <h2 className="font-oswald text-white/20 text-xs uppercase tracking-widest mb-3 pl-1">Security & Compliance</h2>

        <Section icon={Lock} title="Security Features">
          <Feature title="Rate Limiting" desc="API protection via slowapi (e.g., 5 registrations/minute)" />
          <Feature title="JWT Authentication" desc="Separate tokens for participants and admin roles" />
          <Feature title="Server-Side Payment Validation" desc="Whop license keys strictly validated — no fake codes accepted" />
          <Feature title="Password Hashing" desc="Bcrypt for all stored passwords" />
          <Feature title="FCRA-Compliant Background Checks" desc="Checkr handles all sensitive PII on their secure platform" />
          <Feature title="Legal Pages" desc="Terms of Service, Privacy Policy, About — linked from every page" />
        </Section>
      </div>

      {/* ===== INTEGRATIONS ===== */}
      <div className="mb-6">
        <h2 className="font-oswald text-white/20 text-xs uppercase tracking-widest mb-3 pl-1">Integrations</h2>

        <Section icon={Zap} title="Third-Party Services">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead><TableRow header cells={['Service', 'Purpose', 'Key?', 'Provider']} /></thead>
              <tbody>
                <TableRow cells={['Brevo', 'Email, SMS, Inbound Parsing, Analytics', 'User Key', 'Client']} />
                <TableRow cells={['Whop', 'Payment Processing & Validation', 'User Key', 'Client']} />
                <TableRow cells={['HeyGen', 'AI Video Testimonials', 'User Key', 'Client']} />
                <TableRow cells={['Checkr', 'Background Check Screening', 'User Key', 'Client']} />
                <TableRow cells={['OpenAI (GPT-4o)', 'AI Chat, LLC Assistant, Email Auto-Responder', 'Included', 'Pre-configured']} />
                <TableRow cells={['Sora 2', 'Landing Page Marketing Video', 'Included', 'Pre-configured']} />
                <TableRow cells={['Jitsi Meet', 'Video Conferencing + Breakout Rooms', 'None', 'Free']} />
              </tbody>
            </table>
          </div>
        </Section>

        <Section icon={Globe} title="Webhook Endpoints">
          <p className="text-white/40 text-sm mb-3">Configure these URLs in each service's dashboard:</p>
          <div className="space-y-2">
            {[
              { svc: 'Whop', url: '/api/webhooks/whop', events: 'membership.went_valid' },
              { svc: 'Brevo', url: '/api/webhooks/brevo', events: 'Inbound email, delivery status' },
              { svc: 'Checkr', url: '/api/webhooks/checkr', events: 'invitation.completed, report.completed' },
            ].map(w => (
              <div key={w.svc} className="p-3 bg-white/[0.03] border border-white/[0.04] rounded-lg">
                <p className="text-white/70 text-sm font-medium">{w.svc}</p>
                <p className="text-rtb-purple text-xs font-mono break-all">{process.env.REACT_APP_BACKEND_URL}{w.url}</p>
                <p className="text-white/30 text-xs mt-1">Events: {w.events}</p>
              </div>
            ))}
          </div>
        </Section>
      </div>

      {/* ===== GOING LIVE ===== */}
      <div className="mb-6">
        <h2 className="font-oswald text-white/20 text-xs uppercase tracking-widest mb-3 pl-1">Going Live Checklist</h2>

        <Section icon={Rocket} title="What the Client Needs to Provide" defaultOpen={true}>
          <div className="space-y-3">
            {[
              { num: 1, title: 'Brevo Account', desc: 'API key for email/SMS delivery (free tier available). Enter in Settings → Notifications.' },
              { num: 2, title: 'Whop Account', desc: 'Product set up at $75, API key for server-side validation. Enter in Settings → Payment.' },
              { num: 3, title: 'HeyGen Account (optional)', desc: 'API key for AI video testimonials. Enter in Settings → AI Video.' },
              { num: 4, title: 'Checkr Account (optional)', desc: 'API key for background checks. Enter in Settings → Background Checks.' },
              { num: 5, title: 'DNS Configuration', desc: 'MX records for reply.readytobuildit.life → Brevo for inbound email parsing.' },
              { num: 6, title: 'Social Media Pages', desc: 'Facebook and Instagram URLs. Enter in Settings → Social.' },
            ].map(item => (
              <div key={item.num} className="flex items-start gap-3 p-3 bg-white/[0.03] rounded-lg">
                <span className="w-6 h-6 bg-rtb-purple/20 text-rtb-purple rounded-full flex items-center justify-center text-xs font-bold flex-shrink-0">{item.num}</span>
                <div>
                  <p className="text-white/80 text-sm font-medium">{item.title}</p>
                  <p className="text-white/40 text-xs">{item.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </Section>
      </div>

      {/* ===== MARKET COMPARISON ===== */}
      <div className="mb-6">
        <h2 className="font-oswald text-white/20 text-xs uppercase tracking-widest mb-3 pl-1">Market Comparison</h2>

        <Section icon={Globe} title="Competitive Landscape" defaultOpen={true}>
          <p className="text-white/50 text-sm mb-4">RTBI operates at the intersection of three massive markets. No single competitor covers all three — that's the opportunity.</p>

          <p className="text-rtb-purple text-xs font-bold uppercase tracking-wide mb-2">1. LLC Formation Services ($4B+ market)</p>
          <div className="overflow-x-auto mb-5">
            <table className="w-full text-sm">
              <thead><TableRow header cells={['Competitor', 'Price', 'What They Do', 'What They DON\'T Do']} /></thead>
              <tbody>
                <TableRow cells={['LegalZoom', '$0-$299 + state fees', 'File LLC docs, EIN, registered agent, attorney access', 'No education, no mentorship, no credit building, no funding guidance']} />
                <TableRow cells={['ZenBusiness', '$0-$299 + state fees', 'Fast LLC filing, compliance alerts, operating agreement', 'No business education, no community, no coaching']} />
                <TableRow cells={['Incfile (Bizee)', '$0 + state fees', 'Free tier LLC filing, registered agent year 1', 'Zero education or support beyond paperwork']} />
                <TableRow cells={[<span className="text-rtb-purple font-bold">RTBI</span>, '$75 total', 'LLC formation in all 50 states + AI fee waivers + 20-day mentorship + credit building + $250K funding strategy', 'Full program — not just paperwork']} />
              </tbody>
            </table>
          </div>
          <div className="p-3 bg-rtb-purple/5 border border-rtb-purple/10 rounded-lg mb-5">
            <p className="text-rtb-purple text-xs font-bold mb-1">RTBI Advantage:</p>
            <p className="text-white/60 text-sm">LegalZoom charges $249/yr just for a registered agent. ZenBusiness charges $199/yr for compliance. RTBI gives you the LLC filing <strong className="text-white/80">plus</strong> the entire business education, AI assistant, credit strategy, and funding roadmap for a one-time $75. They sell paperwork — RTBI sells transformation.</p>
          </div>

          <p className="text-rtb-purple text-xs font-bold uppercase tracking-wide mb-2">2. Entrepreneurship Education ($1.5B market, 11.2% CAGR)</p>
          <div className="overflow-x-auto mb-5">
            <table className="w-full text-sm">
              <thead><TableRow header cells={['Competitor', 'Price', 'What They Do', 'What They DON\'T Do']} /></thead>
              <tbody>
                <TableRow cells={['Skool', '$99/mo (creator pays)', 'Community + courses + gamification. Creator sets member price.', 'No LLC formation, no payment validation, no AI assistant, no background checks']} />
                <TableRow cells={['Kajabi', '$149-$399/mo', 'All-in-one: courses, email, funnels, website', 'Poor community features, no LLC tools, no video conferencing, no AI']} />
                <TableRow cells={['Foundr', '$1,497+ per course', 'High-quality entrepreneurship courses', 'One-way content only — no community, no tools, no ongoing support']} />
                <TableRow cells={[<span className="text-rtb-purple font-bold">RTBI</span>, '$75 one-time', 'Structured 20-day program + community + AI tools + LLC + gamification + live video', 'All-in-one, self-contained platform']} />
              </tbody>
            </table>
          </div>
          <div className="p-3 bg-rtb-purple/5 border border-rtb-purple/10 rounded-lg mb-5">
            <p className="text-rtb-purple text-xs font-bold mb-1">RTBI Advantage:</p>
            <p className="text-white/60 text-sm">Skool has great gamification but you still need external tools for payments, email, video, and LLC. Kajabi is $399/mo with no community. Foundr sells $1,500 courses with no ongoing support. RTBI is a <strong className="text-white/80">self-contained ecosystem</strong> at a fraction of the cost with Brevo email/SMS, Jitsi video, Whop payments, AI chatbot, and Checkr background checks all built in.</p>
          </div>

          <p className="text-rtb-purple text-xs font-bold uppercase tracking-wide mb-2">3. Business Coaching / Mentorship ($500-$5,000/mo market)</p>
          <div className="overflow-x-auto mb-5">
            <table className="w-full text-sm">
              <thead><TableRow header cells={['Model', 'Typical Price', 'Format', 'Limitation']} /></thead>
              <tbody>
                <TableRow cells={['1-on-1 Coaching', '$200-$600/hour', 'Live calls, personalized', 'Doesn\'t scale. Coach burns out at 20 clients.']} />
                <TableRow cells={['Group Mastermind', '$1,500-$8,000 total', 'Weekly group calls, community', 'No structured curriculum. Quality varies wildly.']} />
                <TableRow cells={['Online Course', '$497-$2,000 one-time', 'Video content, maybe Q&A', 'No accountability. 90%+ never finish.']} />
                <TableRow cells={[<span className="text-rtb-purple font-bold">RTBI</span>, '$75 one-time', 'Structured daily program + AI + live video + gamification + automation', 'Scales infinitely. AI handles personalization. Gamification drives completion.']} />
              </tbody>
            </table>
          </div>
          <div className="p-3 bg-rtb-purple/5 border border-rtb-purple/10 rounded-lg">
            <p className="text-rtb-purple text-xs font-bold mb-1">RTBI Advantage:</p>
            <p className="text-white/60 text-sm">Traditional coaching costs $500-$5,000/month and doesn't scale. RTBI automates the 80% (daily drip emails, AI assistant, gamification, progress tracking) so the admin can focus on the 20% that requires human touch (live mastery sessions via Jitsi, 1-on-1 Calendly calls). <strong className="text-white/80">$75 entry point removes all barriers. Automation makes it profitable at scale.</strong></p>
          </div>
        </Section>
      </div>

      {/* ===== DISRUPTION STRATEGY ===== */}
      <div className="mb-6">
        <h2 className="font-oswald text-white/20 text-xs uppercase tracking-widest mb-3 pl-1">Disruption Strategy</h2>

        <Section icon={Zap} title="What Makes RTBI a Market Disruptor" defaultOpen={true}>
          <p className="text-white/50 text-sm mb-4">RTBI already has a strong foundation. Here's what separates it from the pack and what to double down on:</p>

          <div className="space-y-4">
            <div className="p-4 bg-rtb-green/5 border border-rtb-green/10 rounded-xl">
              <p className="text-rtb-green font-bold text-sm mb-1">1. Price Disruption — $75 vs. $500-$5,000</p>
              <p className="text-white/50 text-sm">Every competitor charges 10x-100x more for less. The $75 price point isn't a weakness — it's a weapon. Low barrier = massive volume. At 1,000 enrollees/month that's $75K/month recurring with near-zero marginal cost because automation handles delivery.</p>
            </div>

            <div className="p-4 bg-rtb-green/5 border border-rtb-green/10 rounded-xl">
              <p className="text-rtb-green font-bold text-sm mb-1">2. All-in-One Ecosystem (Nobody Else Has This)</p>
              <p className="text-white/50 text-sm">LegalZoom does LLC. Skool does community. Kajabi does courses. Brevo does email. Checkr does background checks. Jitsi does video. <strong className="text-white/70">RTBI does ALL of them in one platform.</strong> A participant never leaves. That's the moat — every feature creates switching cost.</p>
            </div>

            <div className="p-4 bg-rtb-green/5 border border-rtb-green/10 rounded-xl">
              <p className="text-rtb-green font-bold text-sm mb-1">3. AI-First Where Competitors Are Manual</p>
              <p className="text-white/50 text-sm">The AI LLC Assistant writes hardship letters, guides filing, generates operating agreements. The AI Email Responder handles inbound support. The AI chatbot pre-qualifies and educates. Competitors use humans for all of this — which means higher costs and slower response. RTBI's AI runs 24/7 at zero marginal cost.</p>
            </div>

            <div className="p-4 bg-rtb-green/5 border border-rtb-green/10 rounded-xl">
              <p className="text-rtb-green font-bold text-sm mb-1">4. Gamification Drives Completion (Skool's Playbook, Better)</p>
              <p className="text-white/50 text-sm">Online courses have 5-15% completion rates. RTBI's milestone badges, XP system, streak tracking, and daily drip emails create accountability loops. Skool pioneered this in communities — RTBI applies it to a structured program with specific business outcomes (LLC formed, EIN obtained, first revenue).</p>
            </div>

            <div className="p-4 bg-rtb-green/5 border border-rtb-green/10 rounded-xl">
              <p className="text-rtb-green font-bold text-sm mb-1">5. Automation = Infinite Scale</p>
              <p className="text-white/50 text-sm">20-day email drip runs on autopilot. Payment validation is automated. Contract signing is self-service. Background checks are webhook-driven. The admin's only manual tasks are live mastery sessions and reviewing flagged emails. One admin can serve 10,000 participants.</p>
            </div>
          </div>
        </Section>

        <Section icon={Rocket} title="What to Build Next to Dominate">
          <p className="text-white/50 text-sm mb-4">These are the features that will separate RTBI from "good platform" to "category killer":</p>

          <div className="space-y-3">
            <div className="p-4 bg-white/[0.03] border border-white/[0.06] rounded-xl">
              <div className="flex items-center gap-2 mb-2">
                <span className="px-2 py-0.5 bg-red-500/10 text-red-400 text-[10px] font-bold rounded-full">HIGH IMPACT</span>
                <p className="text-white font-bold text-sm">Community Feed & Peer Accountability</p>
              </div>
              <p className="text-white/50 text-sm">Skool's #1 feature is community. Add a feed where participants post progress, celebrate milestones, and help each other. Participants who see peers succeeding are 3x more likely to complete. This is the difference between a course and a movement.</p>
            </div>

            <div className="p-4 bg-white/[0.03] border border-white/[0.06] rounded-xl">
              <div className="flex items-center gap-2 mb-2">
                <span className="px-2 py-0.5 bg-red-500/10 text-red-400 text-[10px] font-bold rounded-full">HIGH IMPACT</span>
                <p className="text-white font-bold text-sm">Outcome Tracking & Success Metrics</p>
              </div>
              <p className="text-white/50 text-sm">Track real results: "342 LLCs formed this month", "87% completion rate", "$1.2M in business funding secured by graduates." Show these on the landing page. Social proof with real numbers is the most powerful marketing tool in existence. LegalZoom can't show this because they don't know what happens after the LLC is filed.</p>
            </div>

            <div className="p-4 bg-white/[0.03] border border-white/[0.06] rounded-xl">
              <div className="flex items-center gap-2 mb-2">
                <span className="px-2 py-0.5 bg-amber-500/10 text-amber-400 text-[10px] font-bold rounded-full">REVENUE</span>
                <p className="text-white font-bold text-sm">Tiered Programs & Upsells</p>
              </div>
              <p className="text-white/50 text-sm">$75 is the entry. After graduation, offer: Advanced Mastery ($297), 1-on-1 Coaching Package ($997), Annual Membership for ongoing support ($29/mo). The 20-day program is the top of the funnel. Revenue scales with depth, not just volume. Graduates who've already seen results will gladly invest more.</p>
            </div>

            <div className="p-4 bg-white/[0.03] border border-white/[0.06] rounded-xl">
              <div className="flex items-center gap-2 mb-2">
                <span className="px-2 py-0.5 bg-amber-500/10 text-amber-400 text-[10px] font-bold rounded-full">REVENUE</span>
                <p className="text-white font-bold text-sm">Referral / Affiliate Engine</p>
              </div>
              <p className="text-white/50 text-sm">Give graduates a unique referral link. 20% commission ($15 per signup). Skool's entire growth engine is organic referrals through community. A graduate who formed an LLC and started earning is the best salesperson RTBI could ever have. Cost of acquisition drops to near zero.</p>
            </div>

            <div className="p-4 bg-white/[0.03] border border-white/[0.06] rounded-xl">
              <div className="flex items-center gap-2 mb-2">
                <span className="px-2 py-0.5 bg-blue-500/10 text-blue-400 text-[10px] font-bold rounded-full">MOAT</span>
                <p className="text-white font-bold text-sm">Mobile App (PWA)</p>
              </div>
              <p className="text-white/50 text-sm">68% of course consumption is on mobile. A Progressive Web App with push notifications ("Day 6 is unlocked! You're on a 5-day streak!") will crush completion rates. Skool doesn't have a native app. Kajabi's app is clunky. Being mobile-first is a competitive advantage few mentorship platforms have nailed.</p>
            </div>

            <div className="p-4 bg-white/[0.03] border border-white/[0.06] rounded-xl">
              <div className="flex items-center gap-2 mb-2">
                <span className="px-2 py-0.5 bg-blue-500/10 text-blue-400 text-[10px] font-bold rounded-full">MOAT</span>
                <p className="text-white font-bold text-sm">AI-Powered Personalization</p>
              </div>
              <p className="text-white/50 text-sm">The questionnaire already generates a Recommended Path. Take it further: adapt daily content based on the path, send different drip emails for Real Estate vs. Online Business participants, and have the AI assistant give state-specific and industry-specific advice. Personalization at scale is something no $5,000 coaching program can match — they'd need 50 coaches.</p>
            </div>

            <div className="p-4 bg-white/[0.03] border border-white/[0.06] rounded-xl">
              <div className="flex items-center gap-2 mb-2">
                <span className="px-2 py-0.5 bg-rtb-purple/20 text-rtb-purple text-[10px] font-bold rounded-full">TRUST</span>
                <p className="text-white font-bold text-sm">Verified Graduate Testimonials</p>
              </div>
              <p className="text-white/50 text-sm">Use HeyGen AI testimonials as placeholders now, but build a pipeline where real graduates submit video testimonials. Display them with verified badges ("Completed 20-Day Program", "LLC Formed in Texas"). Real proof from real people is what converts at the $75 price point — people need to trust that it works.</p>
            </div>
          </div>
        </Section>

        <Section icon={Target} title="The Bottom Line">
          <div className="p-5 bg-gradient-to-br from-rtb-purple/10 to-rtb-green/10 border border-rtb-purple/20 rounded-xl">
            <p className="text-white font-bold text-base mb-3">RTBI's Disruptor Position:</p>
            <div className="space-y-3 text-white/70 text-sm">
              <p><strong className="text-white">LegalZoom</strong> charges $458+ for LLC paperwork with zero education. <strong className="text-rtb-purple">RTBI gives you the LLC + the business education for $75.</strong></p>
              <p><strong className="text-white">Skool</strong> charges creators $99/mo and members $50-$500/mo for community + courses. <strong className="text-rtb-purple">RTBI is a self-contained platform with community, courses, AI, video, payments, and compliance — all purpose-built for one mission.</strong></p>
              <p><strong className="text-white">Business coaches</strong> charge $500-$5,000/mo and max out at 20 clients. <strong className="text-rtb-purple">RTBI's automation means one admin can serve 10,000 participants at the same quality level.</strong></p>
              <p className="pt-2 border-t border-white/[0.08] text-white/50">The market has LLC tools, education platforms, coaching programs, and community tools — but nobody has combined them into one automated, AI-powered, gamified ecosystem at a $75 price point. That's the disruption.</p>
            </div>
          </div>
        </Section>
      </div>

      <div className="text-center text-white/20 text-xs py-6 border-t border-white/[0.04]">
        ReadyToBuildIt.life Platform v2.0 — Feature Reference & Market Analysis
      </div>
    </AdminLayout>
  );
}
